namespace Serenity {
    import Editor = Decorators.registerEditor;
    import Select2Editor = Serenity.Select2Editor;

    export interface LookupEditorOptions extends Select2EditorOptions {
        lookupKey?: string;
        async?: boolean;
    }

    @Editor("Serenity.LookupEditorBase")
    export class LookupEditorBase<TOptions extends LookupEditorOptions, TItem> extends Select2Editor<TOptions, TItem> {
        constructor(input: JQuery, opt?: TOptions) {
            super(input, opt);

            if (!this.hasAsyncSource()) {
                this.updateItems();
                const self = this;
                Q.ScriptData.bindToChange(`Lookup.${this.getLookupKey()}`, this.uniqueName, function () {
                    self.updateItems();
                });
            }
        }

        hasAsyncSource(): boolean {
            return !!this.options.async;
        }

        protected initializeAsync(): PromiseLike<void> {
            return this.updateItemsAsync().then(() => {
                Q.ScriptData.bindToChange(`Lookup.${this.getLookupKey()}`, this.uniqueName, () => {
                    this.updateItemsAsync();
                });
            }, null);
        }

        destroy(): void {
            if (!this.hasAsyncSource()) {
                Q.ScriptData.unbindFromChange(this.uniqueName);
            }

            super.destroy();
        }

        protected lookup: Q.Lookup<TItem>;

        protected getIdField() {
            return this.lookup != null ? this.lookup.idField : super.getIdField();
        }

        protected mapItem(item: TItem) {
            return {
                id: this.itemId(item),
                text: this.getItemText(item, this.lookup),
                disabled: this.getItemDisabled(item, this.lookup),
                source: item,
            };
        }

        protected getLookupKey(): string {
            if (this.options.lookupKey != null) {
                return this.options.lookupKey;
            }

            var key = (ss as any).getTypeFullName((ss as any).getInstanceType(this));
            var idx = key.indexOf(".");

            if (idx >= 0) {
                key = key.substring(idx + 1);
            }

            if (Q.endsWith(key, "Editor")) {
                key = key.substr(0, key.length - 6);
            }

            return key;
        }

        protected getLookupAsync(): PromiseLike<Q.Lookup<TItem>> {
            return Q.getLookupAsync<TItem>(this.getLookupKey());
        }

        protected getLookup(): Q.Lookup<TItem> {
            return Q.getLookup<TItem>(this.getLookupKey());
        }

        protected getItems(lookup: Q.Lookup<TItem>) {
            return this.filterItems(this.cascadeItems(lookup.items));
        }

        protected getItemText(item: TItem, lookup: Q.Lookup<TItem>) {
            if (lookup == null) {
                return super.itemText(item);
            }

            var textValue = (lookup.textFormatter) ? lookup.textFormatter(item) : item[lookup.textField];

            return (textValue == null) ? "" : textValue.toString();
        }

        protected getItemDisabled(item: TItem, lookup: Q.Lookup<TItem>) {
            return super.itemDisabled(item);
        }

        protected getSelectedData(): TItem {
            var data:any = <TItem>this.element.select2('data');
            if (data)
                return data.source;
            return data;
        }

        public updateItems() {
            this.clearItems();
            this.lookup = this.getLookup();
            var items = this.getItems(this.lookup);

            for (var item of items) {
                this.addItem(this.mapItem(item));
            }
        }

        protected asyncSearch(query: Select2SearchQuery, results: (result: Select2SearchResult<TItem>) => void): Select2SearchPromise {
            return this.getLookupAsync().then(lookup => {
                this.lookup = lookup;

                var items = this.getItems(this.lookup);

                if (query.idList != null) {
                    items = items.filter(x => query.idList.indexOf(this.itemId(x)) >= 0);
                }

                function getText(item: TItem) {
                    return this.getItemText(item, this.lookup);
                }

                items = Select2Editor.filterByText(items, getText.bind(this), query.searchTerm);

                results({
                    items: items.slice(query.skip, query.take),
                    more: items.length >= query.take
                });
            }) as any;
        }

        public updateItemsAsync(): PromiseLike<void> {
            return this.getLookupAsync().then((lookup) => {
                this.clearItems();
                var items = this.getItems(lookup);

                for (var $t1 = 0; $t1 < items.length; $t1++) {
                    var item = items[$t1];
                    var text = this.getItemText(item, lookup);
                    var disabled = this.getItemDisabled(item, lookup);
                    var idValue = item[lookup.idField];
                    var id = ((idValue == null) ? "" : idValue.toString());
                    this.addItem({
                        id: id,
                        text: text,
                        source: item,
                        disabled: disabled,
                    });
                }
            }, null);
        }

        protected getDialogTypeKey() {
            var dialogTypeKey = super.getDialogTypeKey();

            if (dialogTypeKey) {
                return dialogTypeKey;
            }

            return this.getLookupKey();
        }

        protected setCreateTermOnNewEntity(entity: TItem, term: string) {
            entity[this.getLookup().textField] = term;
        }

        protected editDialogDataChange() {
            Q.reloadLookup(this.getLookupKey());
        }
    }

    @Editor("Serenity.Editor")
    export class LookupEditor extends LookupEditorBase<LookupEditorOptions, any> {
        constructor(hidden: JQuery, opt?: LookupEditorOptions) {
            super(hidden, opt);
        }
    }

    /*@Editor("Serenity.Select2AjaxEditor", [IStringValue])
    @Element('<input type="hidden" />')
    export class Select2AjaxEditor<TOptions, TItem> extends Widget<TOptions> implements IStringValue {
        private readonly pageSize: number = 50;

        constructor(hidden: JQuery, opt: TOptions) {
            super(hidden, opt);

            const emptyItemText = this.emptyItemText();

            if (emptyItemText != null) {
                hidden.attr("placeholder", emptyItemText);
            }

            hidden.select2(this.getSelect2Options());
            hidden.attr("type", "text"); // jquery validate to work

            hidden.on(`change.${this.uniqueName}`, (event: JQueryEventObject, x) => {
                if (WX.hasOriginalEvent(event) || !x) {
                    if (ValidationHelper.getValidator(hidden) != null) {
                        hidden.valid();
                    }
                }
            });
        }

        get value(): string {
            return this.get_value();
        }

        set value(v: string) {
            this.set_value(v);
        }

        get_value(): string {
            return (ss as any).safeCast(this.element.select2("val"), String);
        }

        set_value(value: string) {
            if (value !== this.get_value()) {
                var el = this.element;
                el.select2('val', value);
                el.data('select2-change-triggered', true);
                el.triggerHandler('change', [true]);
                el.data('select2-change-triggered', false);
            }
        }

        protected emptyItemText(): string {
            let txt = this.element.attr("placeholder");

            if (txt == null) {
                txt = Q.text("Controls.SelectEditor.EmptyItemText");
            }

            return txt;
        }

        protected getService(): string {
            throw new (ss as any).NotImplementedException();
        }

        protected query(request: ListRequest, callback: (p1: ListResponse<any>) => void): void {
            const options: Q.ServiceOptions<any> = {
                blockUI: false,
                service: `${this.getService()}/List`,
                request: request,
                onSuccess: function (response) {
                    callback(response);
                },
            };
            this.executeQuery(options);
        }

        protected executeQuery(options: ServiceOptions<ListResponse<any>>): void {
            Q.serviceCall(options);
        }

        protected queryByKey(key: string, callback: (p1: any) => void): void {
            const options: Q.ServiceOptions<any> = {
                blockUI: false,
                service: `${this.getService()}/Retrieve`,
                request: {EntityId: key},
                onSuccess: function (response) {
                    callback(response.Entity);
                },
            };
            this.executeQueryByKey(options);
        }

        protected executeQueryByKey(options: ServiceOptions<RetrieveResponse<any>>): void {
            Q.serviceCall(options);
        }

        protected getItemKey(item: any): string {
            return null;
        }

        protected getItemText(item: any): string {
            return null;
        }

        protected getTypeDelay(): number {
            return 500;
        }

        protected getSelect2Options(): Select2Options {
            const emptyItemText = this.emptyItemText();
            let queryTimeout = 0;

            return {
                minimumResultsForSearch: 10,
                placeHolder: (!Q.isEmptyOrNull(emptyItemText) ? emptyItemText : null),
                allowClear: Q.isValue(emptyItemText),
                query: query => {
                    const request = {
                        ContainsText: Q.trimToNull(query.term),
                        Skip: (query.page - 1) * this.pageSize,
                        Take: this.pageSize + 1,
                    };

                    if (queryTimeout !== 0) {
                        window.clearTimeout(queryTimeout);
                    }

                    queryTimeout = window.setTimeout(() => {
                        this.query(request, response => {
                            query.callback({
                                results: response.Entities.slice(0, this.pageSize).map(x => {
                                    return {
                                        id: this.getItemKey(x),
                                        text: this.getItemText(x),
                                        source: x,
                                    };
                                }),
                                more: response.Entities.length >= this.pageSize,
                            });
                        });
                    }, this.getTypeDelay());
                },
                initSelection: (element, callback) => {
                    const val = element.val();

                    if (Q.isEmptyOrNull(val)) {
                        callback(null);

                        return;
                    }

                    this.queryByKey(val, result => {
                        callback((result == null ? null : {
                            id: this.getItemKey(result),
                            text: this.getItemText(result),
                            source: result,
                        }));
                    });
                }
            };
        }

        protected addInplaceCreate(title: string): void {
            const self = this;
            $("<a><b /></a>").addClass("inplace-button inplace-create").attr("title", title)
                .insertAfter(this.element)
                .on("click", function (event: JQueryEventObject) {
                    self.inplaceCreateClick(event);
                });

            this.get_select2Container().add(this.element).addClass("has-inplace-button");
        }

        protected inplaceCreateClick(event: JQueryEventObject): void {
        }

        protected get_select2Container(): JQuery {
            return this.element.prevAll(".select2-container");
        }
    } */
}